(() => {
  if (window.mlRugSizeFinderInitialized) return;

  window.mlRugSizeFinderInitialized = true;

  const state = {
    step: 1,
    room: '',
    widthFeet: '',
    widthInches: '',
    lengthFeet: '',
    lengthInches: '',
    shapePreference: 'auto',
    preference: 'balanced',
    picker: null,
    recommendation: null,
    profile: null,
    availableShapes: []
  };


  /* =========================================================
     HELPERS
     ========================================================= */

  function escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }


  function resetState() {
    state.step = 1;

    state.room = '';

    state.widthFeet = '';
    state.widthInches = '';

    state.lengthFeet = '';
    state.lengthInches = '';

    state.shapePreference = 'auto';

    state.preference = 'balanced';

    state.recommendation = null;

    state.profile = null;

    state.availableShapes = [];
  }


  function roomWidthInches() {
    return (
      (Number(state.widthFeet) || 0) * 12 +
      (Number(state.widthInches) || 0)
    );
  }


  function roomLengthInches() {
    return (
      (Number(state.lengthFeet) || 0) * 12 +
      (Number(state.lengthInches) || 0)
    );
  }


  function normalizeDimensions(
    width,
    length
  ) {
    return {
      width: Math.min(
        width,
        length
      ),

      length: Math.max(
        width,
        length
      )
    };
  }


  /* =========================================================
     PRODUCT PROFILE
     ========================================================= */

  function getProductProfile() {
    if (!state.picker) {
      return {
        hasStandard: false,
        hasRunner: false,
        hasSquare: false,
        hasRound: false,
        hasHalfRound: false,
        hasOval: false,
        roundFocused: false
      };
    }


    const hasStandard =
      Boolean(
        state.picker.querySelector(
          '.ml-size-selector__group--standard'
        )
      );


    const hasRunner =
      Boolean(
        state.picker.querySelector(
          '.ml-size-selector__group--runner'
        )
      );


    const hasSquare =
      Boolean(
        state.picker.querySelector(
          '.ml-size-selector__group--square'
        )
      );


    const hasRound =
      Boolean(
        state.picker.querySelector(
          '.ml-size-selector__group--round'
        )
      );


    const hasHalfRound =
      Boolean(
        state.picker.querySelector(
          '.ml-size-selector__group--half-round'
        )
      );


    const hasOval =
      Boolean(
        state.picker.querySelector(
          '.ml-size-selector__group--oval'
        )
      );


    const roundFocused =
      (
        hasRound ||
        hasHalfRound
      ) &&
      !hasStandard &&
      !hasRunner &&
      !hasSquare &&
      !hasOval;


    return {
      hasStandard,
      hasRunner,
      hasSquare,
      hasRound,
      hasHalfRound,
      hasOval,
      roundFocused
    };
  }


  function getAvailableGroupNames() {
    if (!state.profile) {
      return [];
    }

    const groups = [];

    if (
      state.profile.hasStandard
    ) {
      groups.push('standard');
    }

    if (
      state.profile.hasRunner
    ) {
      groups.push('runner');
    }

    if (
      state.profile.hasSquare
    ) {
      groups.push('square');
    }

    if (
      state.profile.hasRound
    ) {
      groups.push('round');
    }

    if (
      state.profile.hasHalfRound
    ) {
      groups.push('half_round');
    }

    if (
      state.profile.hasOval
    ) {
      groups.push('oval');
    }

    return groups;
  }


  function filterGroupOrder(order) {
    const available =
      getAvailableGroupNames();

    const filtered =
      order.filter(
        (group) =>
          available.includes(group)
      );

    available.forEach(
      (group) => {

        if (
          !filtered.includes(group)
        ) {
          filtered.push(group);
        }

      }
    );

    return filtered;
  }


  /* =========================================================
     SIZE GROUP
     ========================================================= */

  function getGroup(label) {
    const group =
      label.closest(
        '.ml-size-selector__group'
      );

    if (!group) {
      return 'standard';
    }


    if (
      group.classList.contains(
        'ml-size-selector__group--runner'
      )
    ) {
      return 'runner';
    }


    if (
      group.classList.contains(
        'ml-size-selector__group--square'
      )
    ) {
      return 'square';
    }


    if (
      group.classList.contains(
        'ml-size-selector__group--round'
      )
    ) {
      return 'round';
    }


    if (
      group.classList.contains(
        'ml-size-selector__group--half-round'
      )
    ) {
      return 'half_round';
    }


    if (
      group.classList.contains(
        'ml-size-selector__group--oval'
      )
    ) {
      return 'oval';
    }


    return 'standard';
  }


  /* =========================================================
     SIZE PARSING
     ========================================================= */

  function parseDimensions(key) {
    const normalized =
      String(key)
        .toUpperCase()
        .replaceAll('"', '')
        .trim();


    const numbers =
      normalized.match(
        /\d+(?:\.\d+)?/g
      );


    if (
      !numbers ||
      !numbers.length
    ) {
      return null;
    }


    /*
      ROUND
      Example:
      60 ROUND

      Treat as 60 x 60
    */

    if (
      normalized.includes(
        'ROUND'
      ) &&
      !normalized.includes(
        'HALF ROUND'
      ) &&
      numbers.length === 1
    ) {

      const diameter =
        Number(numbers[0]);

      return {
        width: diameter,
        length: diameter
      };
    }


    /*
      HALF ROUND
      Example:
      60 HALF ROUND

      Approximate bounding size:
      60 wide x 30 deep
    */

    if (
      normalized.includes(
        'HALF ROUND'
      ) &&
      numbers.length === 1
    ) {

      const diameter =
        Number(numbers[0]);

      return {
        width: diameter,
        length: diameter / 2
      };
    }


    /*
      NORMAL SIZE
      Example:
      70X102
    */

    if (
      numbers.length >= 2
    ) {

      return {
        width:
          Number(numbers[0]),

        length:
          Number(numbers[1])
      };
    }


    return null;
  }


  function getDisplaySize(
    label,
    key
  ) {
    const span =
      label.querySelector('span');

    if (span) {
      return span.textContent.trim();
    }


    return String(key)
      .replaceAll('"', '')
      .replace(
        /\s*[xX]\s*/g,
        ' × '
      )
      .trim();
  }


  /* =========================================================
     GEOMETRIC SHAPE
     ========================================================= */

  function classifyShape(
    group,
    width,
    length
  ) {

    if (
      group === 'round'
    ) {
      return 'round';
    }


    if (
      group === 'half_round'
    ) {
      return 'half_round';
    }


    if (
      group === 'oval'
    ) {
      return 'oval';
    }


    if (
      group === 'runner'
    ) {
      return 'runner';
    }


    const normalized =
      normalizeDimensions(
        width,
        length
      );


    const ratio =
      normalized.length /
      Math.max(
        normalized.width,
        1
      );


    /*
      Anything within 5% of
      perfectly equal sides is
      treated as square.
    */

    if (ratio <= 1.05) {
      return 'square';
    }


    return 'rectangle';
  }


  /* =========================================================
     READ ACTUAL PRODUCT SIZES
     ========================================================= */

  function getAvailableSizes() {
    if (!state.picker) {
      return [];
    }


    const labels = [
      ...state.picker.querySelectorAll(
        '.ml-size-selector label[data-ml-size]'
      )
    ];


    return labels
      .map(
        (label) => {

          const key =
            label.dataset.mlSize ||
            '';


          const inputId =
            label.getAttribute('for');


          const input =
            inputId
              ? document.getElementById(
                  inputId
                )
              : null;


          if (!input) {
            return null;
          }


          const unavailable =
            input.disabled ||
            input.classList.contains(
              'disabled'
            ) ||
            label.classList.contains(
              'disabled'
            );


          if (unavailable) {
            return null;
          }


          if (
            key
              .toLowerCase()
              .includes('sample')
          ) {
            return null;
          }


          const dimensions =
            parseDimensions(key);


          if (!dimensions) {
            return null;
          }


          const group =
            getGroup(label);


          const normalized =
            normalizeDimensions(
              dimensions.width,
              dimensions.length
            );


          const shape =
            classifyShape(
              group,
              dimensions.width,
              dimensions.length
            );


          return {
            key,

            display:
              getDisplaySize(
                label,
                key
              ),

            group,

            shape,

            width:
              normalized.width,

            length:
              normalized.length,

            area:
              normalized.width *
              normalized.length,

            aspect:
              normalized.length /
              Math.max(
                normalized.width,
                1
              ),

            input,

            label
          };
        }
      )
      .filter(Boolean);
  }


  /* =========================================================
     AVAILABLE SHAPES
     ========================================================= */

  function getMeaningfulShapes() {
    const sizes =
      getAvailableSizes();


    const shapes = [
      ...new Set(
        sizes.map(
          (size) =>
            size.shape
        )
      )
    ];


    const order = [
      'rectangle',
      'square',
      'round',
      'half_round',
      'runner',
      'oval'
    ];


    return order.filter(
      (shape) =>
        shapes.includes(shape)
    );
  }


  function shouldShowShapeStep() {
    return (
      state.availableShapes.length >
      1
    );
  }


  /* =========================================================
     DYNAMIC QUIZ FLOW
     ========================================================= */

  function getFlowSteps() {
    return [
      'room',

      'dimensions',

      ...(
        shouldShowShapeStep()
          ? ['shape']
          : []
      ),

      'preference'
    ];
  }


  function currentStepKey() {
    const flow =
      getFlowSteps();


    return (
      flow[state.step - 1] ||
      'result'
    );
  }


  /* =========================================================
     ROOM OPTIONS
     ========================================================= */

  function getRoomOptions() {
    const profile =
      state.profile ||
      getProductProfile();


    /*
      ROUND / HALF ROUND-ONLY
      PRODUCTS
    */

    if (
      profile.roundFocused
    ) {

      return [
        {
          id: 'living',
          title:
            'Living Room',
          text:
            'Create a centered focal point'
        },

        {
          id: 'dining',
          title:
            'Dining Room',
          text:
            'Center the rug beneath your table'
        },

        {
          id: 'bedroom',
          title:
            'Bedroom',
          text:
            'Add a soft decorative accent'
        },

        {
          id: 'kitchen',
          title:
            'Kitchen',
          text:
            'Style a breakfast nook or sink area'
        },

        {
          id: 'entryway',
          title:
            'Entryway',
          text:
            'Create a welcoming focal point'
        }
      ];
    }


    /*
      REGULAR PRODUCTS
    */

    const rooms = [
      {
        id: 'living',
        title:
          'Living Room',
        text:
          'Define the seating area'
      },

      {
        id: 'dining',
        title:
          'Dining Room',
        text:
          'Ground the table and chairs'
      },

      {
        id: 'bedroom',
        title:
          'Bedroom',
        text:
          'Frame the bed beautifully'
      },

      {
        id: 'kitchen',
        title:
          'Kitchen',

        text:
          profile.hasRunner
            ? 'Choose an area rug or runner'
            : 'Add comfort where you need it'
      },

      {
        id: 'entryway',
        title:
          'Entryway',
        text:
          'Set the tone from the start'
      }
    ];


    /*
      HALLWAY ONLY EXISTS
      WHEN THE PRODUCT HAS RUNNERS
    */

    if (
      profile.hasRunner
    ) {

      rooms.push({
        id: 'hallway',
        title:
          'Hallway',
        text:
          'Find the right runner proportion'
      });
    }


    return rooms;
  }


  /* =========================================================
     AUTOMATIC SHAPE PRIORITY
     ========================================================= */

  function getPreferredGroups(
    room,
    roomWidth,
    roomLength
  ) {

    const dimensions =
      normalizeDimensions(
        roomWidth,
        roomLength
      );


    const ratio =
      dimensions.length /
      Math.max(
        dimensions.width,
        1
      );


    const profile =
      state.profile ||
      getProductProfile();


    /*
      ROUND-FOCUSED PRODUCT
    */

    if (
      profile.roundFocused
    ) {

      switch (room) {

        case 'kitchen':

          return filterGroupOrder([
            'half_round',
            'round'
          ]);


        case 'entryway':
        case 'dining':
        case 'bedroom':
        case 'living':
        default:

          return filterGroupOrder([
            'round',
            'half_round'
          ]);
      }
    }


    /*
      HALLWAY
    */

    if (
      room === 'hallway'
    ) {

      return filterGroupOrder([
        'runner',
        'standard'
      ]);
    }


    /*
      KITCHEN
    */

    if (
      room === 'kitchen'
    ) {

      if (
        profile.hasRunner &&
        ratio >= 1.65
      ) {

        return filterGroupOrder([
          'runner',
          'standard',
          'square'
        ]);
      }


      if (
        ratio <= 1.15
      ) {

        return filterGroupOrder([
          'square',
          'standard',
          'round',
          'runner'
        ]);
      }


      return filterGroupOrder([
        'standard',
        'square',
        'runner'
      ]);
    }


    /*
      ENTRYWAY
    */

    if (
      room === 'entryway'
    ) {

      if (
        ratio <= 1.15
      ) {

        return filterGroupOrder([
          'square',
          'round',
          'standard',
          'half_round'
        ]);
      }


      return filterGroupOrder([
        'standard',
        'square',
        'round',
        'half_round'
      ]);
    }


    /*
      DINING ROOM
    */

    if (
      room === 'dining'
    ) {

      if (
        ratio <= 1.15
      ) {

        return filterGroupOrder([
          'square',
          'round',
          'standard',
          'oval'
        ]);
      }


      return filterGroupOrder([
        'standard',
        'oval',
        'square',
        'round'
      ]);
    }


    /*
      BEDROOM
    */

    if (
      room === 'bedroom'
    ) {

      if (
        ratio <= 1.15
      ) {

        return filterGroupOrder([
          'square',
          'standard'
        ]);
      }


      return filterGroupOrder([
        'standard',
        'square'
      ]);
    }


    /*
      LIVING ROOM

      Square / near-square room:
      square and round are naturally
      better proportion matches.
    */

    if (
      ratio <= 1.15
    ) {

      return filterGroupOrder([
        'square',
        'round',
        'standard'
      ]);
    }


    return filterGroupOrder([
      'standard',
      'square',
      'round'
    ]);
  }


  /* =========================================================
     TARGET RUG DIMENSIONS
     ========================================================= */

  function getTargetDimensions(
    room,
    roomWidth,
    roomLength,
    preference
  ) {

    const dimensions =
      normalizeDimensions(
        roomWidth,
        roomLength
      );


    let sideMargin;
    let endMargin;


    /*
      HALLWAY
    */

    if (
      room === 'hallway'
    ) {

      if (
        preference === 'open'
      ) {

        sideMargin = 6;
        endMargin = 10;
      }

      else if (
        preference === 'coverage'
      ) {

        sideMargin = 2;
        endMargin = 4;
      }

      else {

        sideMargin = 4;
        endMargin = 7;
      }
    }


    /*
      KITCHEN
    */

    else if (
      room === 'kitchen'
    ) {

      const ratio =
        dimensions.length /
        Math.max(
          dimensions.width,
          1
        );


      if (
        state.profile?.hasRunner &&
        ratio >= 1.65
      ) {

        if (
          preference === 'open'
        ) {

          sideMargin = 8;
          endMargin = 12;
        }

        else if (
          preference === 'coverage'
        ) {

          sideMargin = 4;
          endMargin = 5;
        }

        else {

          sideMargin = 6;
          endMargin = 8;
        }
      }


      else if (
        preference === 'open'
      ) {

        sideMargin = 18;
        endMargin = 18;
      }


      else if (
        preference === 'coverage'
      ) {

        sideMargin = 8;
        endMargin = 8;
      }


      else {

        sideMargin = 12;
        endMargin = 12;
      }
    }


    /*
      ENTRYWAY
    */

    else if (
      room === 'entryway'
    ) {

      if (
        preference === 'open'
      ) {

        sideMargin = 18;
        endMargin = 18;
      }

      else if (
        preference === 'coverage'
      ) {

        sideMargin = 8;
        endMargin = 8;
      }

      else {

        sideMargin = 12;
        endMargin = 12;
      }
    }


    /*
      LIVING / DINING / BEDROOM
    */

    else {

      if (
        preference === 'open'
      ) {

        sideMargin = 24;
        endMargin = 24;
      }

      else if (
        preference === 'coverage'
      ) {

        sideMargin = 12;
        endMargin = 12;
      }

      else {

        sideMargin = 18;
        endMargin = 18;
      }
    }


    return {

      width:
        Math.max(
          18,
          dimensions.width -
          sideMargin * 2
        ),

      length:
        Math.max(
          24,
          dimensions.length -
          endMargin * 2
        )
    };
  }


  /* =========================================================
     CUSTOM-SIZE REQUEST
     ========================================================= */

  function getCustomRequestElement() {

    if (!state.picker) {
      return null;
    }


    const productScope =
      state.picker.closest(
        'product-info'
      );


    return (
      state.picker.querySelector(
        'ml-custom-size-request'
      ) ||

      productScope?.querySelector(
        'ml-custom-size-request'
      ) ||

      null
    );
  }


  function getCustomRecommendation(
    target,
    best
  ) {

    if (
      !target ||
      !best ||
      !getCustomRequestElement()
    ) {
      return null;
    }


    /*
      The request form currently accepts
      width and length. Keep round,
      half-round, and oval recommendations
      tied to their actual standard options.
    */

    if (
      [
        'round',
        'half_round',
        'oval'
      ].includes(best.shape)
    ) {
      return null;
    }


    const normalizedTarget =
      normalizeDimensions(
        Math.round(target.width),
        Math.round(target.length)
      );


    let width =
      Math.min(
        120,
        Math.max(
          12,
          normalizedTarget.width
        )
      );


    let length =
      Math.max(
        18,
        normalizedTarget.length
      );


    /*
      Keep a square recommendation square.
    */

    if (
      best.shape === 'square' ||
      state.shapePreference === 'square'
    ) {

      const side =
        Math.min(
          120,
          Math.max(
            18,
            Math.round(
              Math.min(
                target.width,
                target.length
              )
            )
          )
        );


      width = side;
      length = side;
    }


    const exactStandardExists =
      getAvailableSizes()
        .some(
          (size) =>
            size.width === width &&
            size.length === length
        );


    if (exactStandardExists) {
      return null;
    }


    const widthGap =
      Math.abs(
        best.width - width
      );


    const lengthGap =
      Math.abs(
        best.length - length
      );


    const customArea =
      width * length;


    const areaGap =
      Math.abs(
        best.area - customArea
      ) /
      Math.max(
        customArea,
        1
      );


    /*
      Prefer the familiar standard option
      when it is already close. Recommend
      custom only when the ideal dimensions
      are materially different.
    */

    const materiallyDifferent =
      (
        widthGap >= 8 &&
        lengthGap >= 8
      ) ||
      widthGap >= 14 ||
      lengthGap >= 18 ||
      areaGap >= 0.22;


    if (!materiallyDifferent) {
      return null;
    }


    return {
      width,
      length,
      display:
        `${width} × ${length} in.`
    };
  }


  /* =========================================================
     SHAPE PREFERENCE
     ========================================================= */

  function getShapePreferencePenalty(
    candidate
  ) {

    /*
      No need for shape scoring
      if there is only one shape.
    */

    if (
      !shouldShowShapeStep()
    ) {
      return 0;
    }


    /*
      Customer asked us to choose.
    */

    if (
      state.shapePreference ===
      'auto'
    ) {
      return 0;
    }


    /*
      Exact preferred shape.
    */

    if (
      candidate.shape ===
      state.shapePreference
    ) {
      return 0;
    }


    /*
      Strong preference penalty,
      but not so strong that we
      recommend something that
      physically cannot fit.
    */

    return 8;
  }


  /* =========================================================
     SCORE EACH SIZE
     ========================================================= */

  function scoreCandidate(
    candidate,
    target,
    roomDimensions,
    groupPriority
  ) {

    const widthDifference =
      Math.abs(
        candidate.width -
        target.width
      ) /
      Math.max(
        target.width,
        1
      );


    const lengthDifference =
      Math.abs(
        candidate.length -
        target.length
      ) /
      Math.max(
        target.length,
        1
      );


    const targetArea =
      target.width *
      target.length;


    const areaDifference =
      Math.abs(
        candidate.area -
        targetArea
      ) /
      Math.max(
        targetArea,
        1
      );


    /*
      IMPORTANT:
      COMPARE PROPORTIONS

      Example:
      120x120 = aspect 1.00
      120x175 = aspect 1.46

      In a square room, the square
      rug now receives a major
      advantage.
    */

    const targetAspect =
      target.length /
      Math.max(
        target.width,
        1
      );


    const aspectDifference =
      Math.abs(
        candidate.aspect -
        targetAspect
      ) /
      Math.max(
        targetAspect,
        1
      );


    let overflowPenalty = 0;


    if (
      candidate.width >
      roomDimensions.width
    ) {
      overflowPenalty += 20;
    }


    if (
      candidate.length >
      roomDimensions.length
    ) {
      overflowPenalty += 20;
    }


    const shapePreferencePenalty =
      getShapePreferencePenalty(
        candidate
      );


    return (
      widthDifference +

      lengthDifference +

      areaDifference * 0.35 +

      /*
        Shape/proportion matters
        substantially now.
      */

      aspectDifference * 2.4 +

      groupPriority +

      shapePreferencePenalty +

      overflowPenalty
    );
  }


  /* =========================================================
     CALCULATE RESULT
     ========================================================= */

  function calculateRecommendation() {
    const allSizes =
      getAvailableSizes();


    if (!allSizes.length) {
      return null;
    }


    const rawWidth =
      roomWidthInches();


    const rawLength =
      roomLengthInches();


    const roomDimensions =
      normalizeDimensions(
        rawWidth,
        rawLength
      );


    const preferredGroups =
      getPreferredGroups(
        state.room,
        rawWidth,
        rawLength
      );


    const target =
      getTargetDimensions(
        state.room,
        rawWidth,
        rawLength,
        state.preference
      );


    const scored =
      allSizes
        .map(
          (candidate) => {

            let groupPriority =
              preferredGroups.indexOf(
                candidate.group
              );


            if (
              groupPriority === -1
            ) {

              groupPriority = 4;
            }

            else {

              groupPriority *= 0.3;
            }


            return {
              ...candidate,

              score:
                scoreCandidate(
                  candidate,
                  target,
                  roomDimensions,
                  groupPriority
                )
            };
          }
        )
        .sort(
          (a, b) =>
            a.score - b.score
        );


    const best =
      scored[0];


    if (!best) {
      return null;
    }


    /*
      Smaller / larger alternatives
      remain the SAME SHAPE as
      the chosen recommendation.
    */

    const sameShape =
      scored.filter(
        (candidate) =>
          candidate.shape ===
          best.shape
      );


    const smallerCandidates =
      sameShape
        .filter(
          (candidate) =>
            candidate.area <
            best.area
        )
        .sort(
          (a, b) =>
            b.area - a.area
        );


    const largerCandidates =
      sameShape
        .filter(
          (candidate) =>
            candidate.area >
              best.area &&

            candidate.width <=
              roomDimensions.width &&

            candidate.length <=
              roomDimensions.length
        )
        .sort(
          (a, b) =>
            a.area - b.area
        );


    const custom =
      getCustomRecommendation(
        target,
        best
      );


    return {
      best,

      custom,

      smaller:
        smallerCandidates[0] ||
        null,

      larger:
        largerCandidates[0] ||
        null,

      roomDimensions,

      target
    };
  }


  /* =========================================================
     RESULT TEXT
     ========================================================= */

  function resultDescription() {

    if (
      state.preference === 'open'
    ) {

      return (
        'This size keeps more of your flooring visible ' +
        'for a lighter, more open look.'
      );
    }


    if (
      state.preference ===
      'coverage'
    ) {

      return (
        'This size gives the room fuller coverage ' +
        'and a more substantial rug presence.'
      );
    }


    return (
      'This size offers a balanced proportion ' +
      'with comfortable flooring visible around the rug.'
    );
  }


  function roomLabel() {

    const names = {

      living:
        'Living Room',

      dining:
        'Dining Room',

      bedroom:
        'Bedroom',

      kitchen:
        'Kitchen',

      entryway:
        'Entryway',

      hallway:
        'Hallway'
    };


    return (
      names[state.room] ||
      'Your Room'
    );
  }


  /* =========================================================
     SHAPE COPY
     ========================================================= */

  function shapeLabel(shape) {

    const labels = {

      auto:
        'Best Fit for My Space',

      rectangle:
        'Rectangle',

      square:
        'Square',

      round:
        'Round',

      half_round:
        'Half Round',

      runner:
        'Runner',

      oval:
        'Oval'
    };


    return (
      labels[shape] ||
      shape
    );
  }


  function shapeDescription(shape) {

    const descriptions = {

      auto:
        'Let us choose the shape that best suits your room proportions.',

      rectangle:
        'A classic rectangular proportion for versatile room layouts.',

      square:
        'A balanced choice for square or nearly square spaces.',

      round:
        'A softer shape that works beautifully in centered spaces.',

      half_round:
        'A compact curved shape for doors, sinks, and smaller spaces.',

      runner:
        'A long, narrow proportion for hallways and elongated spaces.',

      oval:
        'A softened elongated shape with graceful proportions.'
    };


    return (
      descriptions[shape] ||
      ''
    );
  }


  /* =========================================================
     MODAL
     ========================================================= */

  const overlay =
    document.createElement(
      'div'
    );


  overlay.className =
    'ml-rsf-overlay';


  overlay.hidden = true;


  overlay.innerHTML = `
    <div
      class="ml-rsf-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="MLRugSizeFinderHeading"
    >

      <div class="ml-rsf-header">

        <div>

          <span class="ml-rsf-eyebrow">
            MONROE &amp; LEISURE
          </span>

          <h2
            id="MLRugSizeFinderHeading"
            class="ml-rsf-heading"
          >
            Find Your Rug Size
          </h2>

        </div>

        <button
          type="button"
          class="ml-rsf-close"
          data-ml-rsf-close
          aria-label="Close rug size finder"
        >
          ×
        </button>

      </div>

      <div
        class="ml-rsf-body"
        data-ml-rsf-body
      ></div>

    </div>
  `;


  /* =========================================================
     PROGRESS
     ========================================================= */

  function progressMarkup() {

    const flow =
      getFlowSteps();


    if (
      state.step >
      flow.length
    ) {
      return '';
    }


    const key =
      currentStepKey();


    const labels = {

      room:
        'CHOOSE YOUR SPACE',

      dimensions:
        'ROOM SIZE',

      shape:
        'RUG SHAPE',

      preference:
        'YOUR PREFERENCE'
    };


    return `
      <div class="ml-rsf-progress">

        <div class="ml-rsf-progress__top">

          <span>
            ${state.step} OF ${flow.length}
          </span>

          <span>
            ${labels[key] || ''}
          </span>

        </div>

        <div class="ml-rsf-progress__track">

          <span
            class="ml-rsf-progress__fill"
            style="
              width:
              ${
                (
                  state.step /
                  flow.length
                ) * 100
              }%;
            "
          ></span>

        </div>

      </div>
    `;
  }


  /* =========================================================
     STEP — ROOM
     ========================================================= */

  function renderRoomStep() {

    const rooms =
      getRoomOptions();


    const introText =
      state.profile?.roundFocused

        ? (
            'Choose the space you’re styling and we’ll ' +
            'recommend a round or half round size that fits beautifully.'
          )

        : (
            'Choose the space you’re styling and we’ll help you ' +
            'find a size that feels right.'
          );


    return `
      ${progressMarkup()}

      <div class="ml-rsf-step">

        <span class="ml-rsf-step__eyebrow">
          YOUR SPACE
        </span>

        <h3 class="ml-rsf-step__heading">
          Where will your rug live?
        </h3>

        <p class="ml-rsf-step__text">
          ${introText}
        </p>


        <div class="ml-rsf-choice-grid">

          ${rooms
            .map(
              (room) => `
                <button
                  type="button"
                  class="
                    ml-rsf-choice
                    ${
                      state.room ===
                      room.id
                        ? 'is-selected'
                        : ''
                    }
                  "
                  data-ml-rsf-room="${room.id}"
                >

                  <span class="ml-rsf-choice__title">
                    ${room.title}
                  </span>

                  <span class="ml-rsf-choice__text">
                    ${room.text}
                  </span>

                </button>
              `
            )
            .join('')}

        </div>


        <div
          class="
            ml-rsf-actions
            ml-rsf-actions--end
          "
        >

          <button
            type="button"
            class="ml-rsf-primary"
            data-ml-rsf-next
            ${
              state.room
                ? ''
                : 'disabled'
            }
          >
            Next: Room Size
          </button>

        </div>

      </div>
    `;
  }


  /* =========================================================
     STEP — DIMENSIONS
     ========================================================= */

  function renderDimensionsStep() {

    return `
      ${progressMarkup()}

      <div class="ml-rsf-step">

        <span class="ml-rsf-step__eyebrow">
          YOUR ROOM
        </span>

        <h3 class="ml-rsf-step__heading">
          What are the approximate dimensions?
        </h3>

        <p class="ml-rsf-step__text">
          A close estimate is all we need.
          Enter the width and length of the room
          to help us narrow down the best proportions.
        </p>


        <div class="ml-rsf-dimensions">

          <div class="ml-rsf-dimension">

            <span class="ml-rsf-dimension__label">
              Room Width
            </span>


            <div class="ml-rsf-dimension__inputs">

              <label class="ml-rsf-number-field">

                <span class="ml-rsf-number-field__label">
                  Feet
                </span>

                <input
                  type="number"
                  min="0"
                  max="50"
                  inputmode="numeric"
                  value="${escapeHtml(
                    state.widthFeet
                  )}"
                  data-ml-rsf-width-feet
                >

              </label>


              <label class="ml-rsf-number-field">

                <span class="ml-rsf-number-field__label">
                  Inches
                </span>

                <input
                  type="number"
                  min="0"
                  inputmode="numeric"
                  value="${escapeHtml(
                    state.widthInches
                  )}"
                  data-ml-rsf-width-inches
                >

              </label>

            </div>

          </div>


          <div class="ml-rsf-dimension">

            <span class="ml-rsf-dimension__label">
              Room Length
            </span>


            <div class="ml-rsf-dimension__inputs">

              <label class="ml-rsf-number-field">

                <span class="ml-rsf-number-field__label">
                  Feet
                </span>

                <input
                  type="number"
                  min="0"
                  max="50"
                  inputmode="numeric"
                  value="${escapeHtml(
                    state.lengthFeet
                  )}"
                  data-ml-rsf-length-feet
                >

              </label>


              <label class="ml-rsf-number-field">

                <span class="ml-rsf-number-field__label">
                  Inches
                </span>

                <input
                  type="number"
                  min="0"
                  inputmode="numeric"
                  value="${escapeHtml(
                    state.lengthInches
                  )}"
                  data-ml-rsf-length-inches
                >

              </label>

            </div>

          </div>

        </div>


        <p
          class="ml-rsf-error"
          data-ml-rsf-error
          hidden
        >
          Please enter both the width and length
          of your room.
        </p>


        <div class="ml-rsf-actions">

          <button
            type="button"
            class="ml-rsf-secondary"
            data-ml-rsf-back
          >
            Back
          </button>


          <button
            type="button"
            class="ml-rsf-primary"
            data-ml-rsf-next
          >
            ${
              shouldShowShapeStep()
                ? 'Next: Rug Shape'
                : 'Next: Your Preference'
            }
          </button>

        </div>

      </div>
    `;
  }


  /* =========================================================
     STEP — SHAPE
     ========================================================= */

  function renderShapeStep() {

    const choices = [
      'auto',
      ...state.availableShapes
    ];


    return `
      ${progressMarkup()}

      <div class="ml-rsf-step">

        <span class="ml-rsf-step__eyebrow">
          RUG SHAPE
        </span>

        <h3 class="ml-rsf-step__heading">
          Do you have a shape in mind?
        </h3>

        <p class="ml-rsf-step__text">
          Choose a shape you prefer,
          or let us decide based on the proportions
          of your room.
        </p>


        <div class="ml-rsf-preferences">

          ${choices
            .map(
              (shape) => `
                <button
                  type="button"
                  class="
                    ml-rsf-preference
                    ${
                      state.shapePreference ===
                      shape
                        ? 'is-selected'
                        : ''
                    }
                  "
                  data-ml-rsf-shape="${shape}"
                >

                  <span
                    class="ml-rsf-preference__marker"
                  ></span>

                  <span
                    class="ml-rsf-preference__content"
                  >

                    <strong>
                      ${shapeLabel(shape)}
                    </strong>

                    <span>
                      ${shapeDescription(shape)}
                    </span>

                  </span>

                </button>
              `
            )
            .join('')}

        </div>


        <div class="ml-rsf-actions">

          <button
            type="button"
            class="ml-rsf-secondary"
            data-ml-rsf-back
          >
            Back
          </button>

          <button
            type="button"
            class="ml-rsf-primary"
            data-ml-rsf-next
          >
            Next: Your Preference
          </button>

        </div>

      </div>
    `;
  }


  /* =========================================================
     STEP — COVERAGE
     ========================================================= */

  function renderPreferenceStep() {

    const preferences = [

      {
        id: 'open',

        title:
          'More Floor Showing',

        text:
          'An airier look with more flooring visible around the rug.'
      },

      {
        id: 'balanced',

        title:
          'Balanced',

        text:
          'A versatile proportion that works beautifully in most spaces.'
      },

      {
        id: 'coverage',

        title:
          'More Coverage',

        text:
          'A fuller look with a stronger presence in the room.'
      }

    ];


    return `
      ${progressMarkup()}

      <div class="ml-rsf-step">

        <span class="ml-rsf-step__eyebrow">
          YOUR PREFERENCE
        </span>

        <h3 class="ml-rsf-step__heading">
          How much of the floor would you like to cover?
        </h3>

        <p class="ml-rsf-step__text">
          Choose the look you prefer
          and we’ll use it to fine-tune
          your recommendation.
        </p>


        <div class="ml-rsf-preferences">

          ${preferences
            .map(
              (preference) => `
                <button
                  type="button"
                  class="
                    ml-rsf-preference
                    ${
                      state.preference ===
                      preference.id
                        ? 'is-selected'
                        : ''
                    }
                  "
                  data-ml-rsf-preference="${preference.id}"
                >

                  <span
                    class="ml-rsf-preference__marker"
                  ></span>

                  <span
                    class="ml-rsf-preference__content"
                  >

                    <strong>
                      ${preference.title}
                    </strong>

                    <span>
                      ${preference.text}
                    </span>

                  </span>

                </button>
              `
            )
            .join('')}

        </div>


        <div class="ml-rsf-actions">

          <button
            type="button"
            class="ml-rsf-secondary"
            data-ml-rsf-back
          >
            Back
          </button>

          <button
            type="button"
            class="ml-rsf-primary"
            data-ml-rsf-next
          >
            Find My Size
          </button>

        </div>

      </div>
    `;
  }


  /* =========================================================
     ALTERNATIVE SIZE
     ========================================================= */

  function alternativeMarkup(
    candidate,
    label
  ) {

    if (!candidate) {
      return '';
    }


    return `
      <button
        type="button"
        class="ml-rsf-alternative"
        data-ml-rsf-select-size="${escapeHtml(
          candidate.key
        )}"
      >

        <span class="ml-rsf-alternative__label">
          ${label}
        </span>

        <strong>
          ${escapeHtml(
            candidate.display
          )}
        </strong>

        <span class="ml-rsf-alternative__action">
          Select
        </span>

      </button>
    `;
  }


  /* =========================================================
     RESULT
     ========================================================= */

  function renderCustomResult(
    result
  ) {

    const custom =
      result.custom;


    if (!custom) {
      return '';
    }


    return `
      <div class="ml-rsf-result">

        <div class="ml-rsf-result__intro">

          <span class="ml-rsf-step__eyebrow">
            YOUR MADE-TO-MEASURE FIT
          </span>

          <h3 class="ml-rsf-result__size">
            ${escapeHtml(
              custom.display
            )}
          </h3>

          <span class="ml-rsf-result__badge">
            Custom Size Recommended
          </span>

          <p class="ml-rsf-result__text">
            Your ideal proportions fall between our standard sizes.
            A made-to-measure rug will give your room the fit and
            amount of floor coverage you selected.
          </p>

        </div>


        <div class="ml-rsf-result__room">

          <span>
            ${roomLabel()}
          </span>

          <span>
            ${Math.round(
              result.roomDimensions.width
            )}
            ×
            ${Math.round(
              result.roomDimensions.length
            )}
            in. room
          </span>

        </div>


        <button
          type="button"
          class="ml-rsf-result__select"
          data-ml-rsf-request-custom
          data-ml-rsf-custom-width="${custom.width}"
          data-ml-rsf-custom-length="${custom.length}"
        >
          Price This Custom Size
        </button>


        <div class="ml-rsf-alternatives">

          <span
            class="ml-rsf-alternatives__heading"
          >
            Prefer a standard size?
          </span>

          <div
            class="ml-rsf-alternatives__grid"
          >

            ${alternativeMarkup(
              result.best,
              'Closest Standard Size'
            )}

          </div>

        </div>


        <button
          type="button"
          class="ml-rsf-start-over"
          data-ml-rsf-start-over
        >
          Start Over
        </button>

      </div>
    `;
  }

  function renderResult() {

    const result =
      state.recommendation;


    if (!result) {

      return `
        <div class="ml-rsf-result">

          <span class="ml-rsf-step__eyebrow">
            SIZE FINDER
          </span>

          <h3 class="ml-rsf-step__heading">
            We couldn't find a matching size.
          </h3>

          <p class="ml-rsf-step__text">
            Try adjusting your measurements,
            or use the Size Guide for additional help.
          </p>

          <button
            type="button"
            class="ml-rsf-primary"
            data-ml-rsf-start-over
          >
            Start Again
          </button>

        </div>
      `;
    }


    const best =
      result.best;


    if (result.custom) {
      return renderCustomResult(
        result
      );
    }


    return `
      <div class="ml-rsf-result">

        <div class="ml-rsf-result__intro">

          <span class="ml-rsf-step__eyebrow">
            OUR RECOMMENDATION
          </span>

          <h3 class="ml-rsf-result__size">
            ${escapeHtml(
              best.display
            )}
          </h3>

          <span class="ml-rsf-result__badge">
            Best Overall Fit
          </span>

          <p class="ml-rsf-result__text">
            ${resultDescription()}
          </p>

        </div>


        <div class="ml-rsf-result__room">

          <span>
            ${roomLabel()}
          </span>

          <span>
            ${Math.round(
              result.roomDimensions.width
            )}
            ×
            ${Math.round(
              result.roomDimensions.length
            )}
            in. room
          </span>

        </div>


        <button
          type="button"
          class="ml-rsf-result__select"
          data-ml-rsf-select-size="${escapeHtml(
            best.key
          )}"
        >
          Select ${escapeHtml(
            best.display
          )}
        </button>


        ${
          result.smaller ||
          result.larger

            ? `
              <div class="ml-rsf-alternatives">

                <span
                  class="
                    ml-rsf-alternatives__heading
                  "
                >
                  Other proportions to consider
                </span>


                <div
                  class="
                    ml-rsf-alternatives__grid
                  "
                >

                  ${alternativeMarkup(
                    result.smaller,
                    'Smaller Option'
                  )}

                  ${alternativeMarkup(
                    result.larger,
                    'Larger Option'
                  )}

                </div>

              </div>
            `

            : ''
        }


        <button
          type="button"
          class="ml-rsf-start-over"
          data-ml-rsf-start-over
        >
          Start Over
        </button>

      </div>
    `;
  }


  /* =========================================================
     RENDER
     ========================================================= */

  function render() {

    const body =
      overlay.querySelector(
        '[data-ml-rsf-body]'
      );


    if (!body) {
      return;
    }


    const step =
      currentStepKey();


    if (
      step === 'room'
    ) {

      body.innerHTML =
        renderRoomStep();
    }


    else if (
      step === 'dimensions'
    ) {

      body.innerHTML =
        renderDimensionsStep();
    }


    else if (
      step === 'shape'
    ) {

      body.innerHTML =
        renderShapeStep();
    }


    else if (
      step === 'preference'
    ) {

      body.innerHTML =
        renderPreferenceStep();
    }


    else {

      body.innerHTML =
        renderResult();
    }
  }


  /* =========================================================
     OPEN
     ========================================================= */

  function openFinder(trigger) {

    resetState();


    state.picker =
      trigger.closest(
        'variant-selects'
      );


    if (!state.picker) {
      return;
    }


    /*
      DETECT THE ACTUAL PRODUCT
      BEFORE BUILDING THE FLOW.
    */

    state.profile =
      getProductProfile();


    state.availableShapes =
      getMeaningfulShapes();


    overlay.hidden = false;


    render();


    requestAnimationFrame(
      () => {

        overlay.classList.add(
          'is-open'
        );


        document.body.classList.add(
          'ml-rsf-open'
        );


        const close =
          overlay.querySelector(
            '[data-ml-rsf-close]'
          );


        if (close) {
          close.focus();
        }
      }
    );
  }


  /* =========================================================
     CLOSE
     ========================================================= */

  function closeFinder() {

    overlay.classList.remove(
      'is-open'
    );


    document.body.classList.remove(
      'ml-rsf-open'
    );


    window.setTimeout(
      () => {

        overlay.hidden = true;

      },
      220
    );
  }


  /* =========================================================
     NEXT
     ========================================================= */

  function nextStep() {

    const key =
      currentStepKey();


    const flow =
      getFlowSteps();


    if (
      key === 'room'
    ) {

      if (!state.room) {
        return;
      }


      state.step += 1;


      render();


      return;
    }


    if (
      key === 'dimensions'
    ) {

      const width =
        roomWidthInches();


      const length =
        roomLengthInches();


      if (
        width <= 0 ||
        length <= 0
      ) {

        const error =
          overlay.querySelector(
            '[data-ml-rsf-error]'
          );


        if (error) {
          error.hidden = false;
        }


        return;
      }


      state.step += 1;


      render();


      return;
    }


    if (
      key === 'shape'
    ) {

      state.step += 1;


      render();


      return;
    }


    if (
      key === 'preference'
    ) {

      state.recommendation =
        calculateRecommendation();


      state.step =
        flow.length + 1;


      render();
    }
  }


  /* =========================================================
     BACK
     ========================================================= */

  function previousStep() {

    if (
      state.step <= 1
    ) {
      return;
    }


    state.step -= 1;


    render();
  }


  /* =========================================================
     SELECT SHOPIFY SIZE
     ========================================================= */

  function selectSize(sizeKey) {

    if (!state.picker) {
      return;
    }


    const labels = [
      ...state.picker.querySelectorAll(
        '.ml-size-selector label[data-ml-size]'
      )
    ];


    const label =
      labels.find(
        (item) =>
          item.dataset.mlSize ===
          sizeKey
      );


    if (!label) {
      return;
    }


    const inputId =
      label.getAttribute('for');


    const input =
      inputId
        ? document.getElementById(
            inputId
          )
        : null;


    if (!input) {
      return;
    }


    if (
      input.disabled ||
      input.classList.contains(
        'disabled'
      )
    ) {
      return;
    }


    if (
      !input.checked
    ) {

      input.click();
    }


    else {

      input.dispatchEvent(
        new Event(
          'change',
          {
            bubbles: true
          }
        )
      );
    }


    const sizeSelector =
      state.picker.querySelector(
        '.ml-size-selector'
      );


    closeFinder();


    if (sizeSelector) {

      window.setTimeout(
        () => {

          sizeSelector.scrollIntoView({

            behavior:
              window
                .matchMedia(
                  '(prefers-reduced-motion: reduce)'
                )
                .matches

                ? 'auto'
                : 'smooth',

            block:
              'center'
          });


          sizeSelector.classList.add(
            'ml-rsf-highlight'
          );


          window.setTimeout(
            () => {

              sizeSelector.classList.remove(
                'ml-rsf-highlight'
              );

            },
            1400
          );

        },
        250
      );
    }
  }


  /* =========================================================
     OPEN CUSTOM REQUEST
     ========================================================= */

  function setRequestInputValue(
    input,
    value
  ) {

    if (!input) {
      return;
    }


    input.value =
      String(value);


    input.dispatchEvent(
      new Event(
        'input',
        {
          bubbles: true
        }
      )
    );


    input.dispatchEvent(
      new Event(
        'change',
        {
          bubbles: true
        }
      )
    );
  }


  function requestCustomSize(
    width,
    length
  ) {

    const request =
      getCustomRequestElement();


    if (!request) {
      return;
    }


    const openButton =
      request.querySelector(
        '[data-ml-custom-request-open]'
      );


    closeFinder();


    window.setTimeout(
      () => {

        if (
          typeof request.open ===
          'function'
        ) {

          request.open();
        }


        else if (openButton) {

          openButton.click();
        }


        window.setTimeout(
          () => {

            const items = [
              ...request.querySelectorAll(
                '[data-ml-custom-request-item]'
              )
            ];


            let item =
              items.find(
                (candidate) => {

                  const widthInput =
                    candidate.querySelector(
                      '[data-ml-custom-request-width]'
                    );


                  const lengthInput =
                    candidate.querySelector(
                      '[data-ml-custom-request-length]'
                    );


                  return (
                    !widthInput?.value.trim() &&
                    !lengthInput?.value.trim()
                  );
                }
              );


            /*
              Preserve any sizes the shopper
              already entered by adding another
              rug instead of overwriting one.
            */

            if (
              !item &&
              typeof request.addItem ===
                'function'
            ) {

              request.addItem();


              const updatedItems =
                request.querySelectorAll(
                  '[data-ml-custom-request-item]'
                );


              item =
                updatedItems[
                  updatedItems.length - 1
                ];
            }


            if (!item) {
              return;
            }


            const widthInput =
              item.querySelector(
                '[data-ml-custom-request-width]'
              );


            const lengthInput =
              item.querySelector(
                '[data-ml-custom-request-length]'
              );


            setRequestInputValue(
              widthInput,
              width
            );


            setRequestInputValue(
              lengthInput,
              length
            );


            if (
              typeof request.updateAllQuotes ===
              'function'
            ) {

              request.updateAllQuotes();
            }


            widthInput?.focus({
              preventScroll: true
            });

          },
          60
        );

      },
      250
    );
  }


  /* =========================================================
     INPUTS
     ========================================================= */

  document.addEventListener(
    'input',
    (event) => {


      if (
        event.target.matches(
          '[data-ml-rsf-width-feet]'
        )
      ) {

        state.widthFeet =
          event.target.value;
      }


      if (
        event.target.matches(
          '[data-ml-rsf-width-inches]'
        )
      ) {

        state.widthInches =
          event.target.value;
      }


      if (
        event.target.matches(
          '[data-ml-rsf-length-feet]'
        )
      ) {

        state.lengthFeet =
          event.target.value;
      }


      if (
        event.target.matches(
          '[data-ml-rsf-length-inches]'
        )
      ) {

        state.lengthInches =
          event.target.value;
      }
    }
  );


  /* =========================================================
     CLICKS
     ========================================================= */

  document.addEventListener(
    'click',
    (event) => {


      /*
        OPEN
      */

      const trigger =
        event.target.closest(
          '[data-ml-size-finder-open]'
        );


      if (trigger) {

        event.preventDefault();


        openFinder(
          trigger
        );


        return;
      }


      /*
        CLOSE
      */

      if (
        event.target.closest(
          '[data-ml-rsf-close]'
        )
      ) {

        event.preventDefault();


        closeFinder();


        return;
      }


      /*
        ROOM
      */

      const room =
        event.target.closest(
          '[data-ml-rsf-room]'
        );


      if (room) {

        event.preventDefault();


        state.room =
          room.dataset.mlRsfRoom;


        render();


        return;
      }


      /*
        SHAPE
      */

      const shape =
        event.target.closest(
          '[data-ml-rsf-shape]'
        );


      if (shape) {

        event.preventDefault();


        state.shapePreference =
          shape.dataset.mlRsfShape;


        render();


        return;
      }


      /*
        COVERAGE
      */

      const preference =
        event.target.closest(
          '[data-ml-rsf-preference]'
        );


      if (preference) {

        event.preventDefault();


        state.preference =
          preference.dataset
            .mlRsfPreference;


        render();


        return;
      }


      /*
        NEXT
      */

      if (
        event.target.closest(
          '[data-ml-rsf-next]'
        )
      ) {

        event.preventDefault();


        nextStep();


        return;
      }


      /*
        BACK
      */

      if (
        event.target.closest(
          '[data-ml-rsf-back]'
        )
      ) {

        event.preventDefault();


        previousStep();


        return;
      }


      /*
        REQUEST CUSTOM SIZE
      */

      const customChoice =
        event.target.closest(
          '[data-ml-rsf-request-custom]'
        );


      if (customChoice) {

        event.preventDefault();


        requestCustomSize(
          Number(
            customChoice.dataset
              .mlRsfCustomWidth
          ),

          Number(
            customChoice.dataset
              .mlRsfCustomLength
          )
        );


        return;
      }


      /*
        SELECT SIZE
      */

      const sizeChoice =
        event.target.closest(
          '[data-ml-rsf-select-size]'
        );


      if (sizeChoice) {

        event.preventDefault();


        selectSize(
          sizeChoice.dataset
            .mlRsfSelectSize
        );


        return;
      }


      /*
        START OVER
      */

      if (
        event.target.closest(
          '[data-ml-rsf-start-over]'
        )
      ) {

        event.preventDefault();


        const picker =
          state.picker;


        const profile =
          state.profile;


        const availableShapes =
          state.availableShapes;


        resetState();


        state.picker =
          picker;


        state.profile =
          profile;


        state.availableShapes =
          availableShapes;


        render();


        return;
      }


      /*
        BACKDROP
      */

      if (
        event.target === overlay &&
        overlay.classList.contains(
          'is-open'
        )
      ) {

        closeFinder();
      }
    }
  );


  /* =========================================================
     ESCAPE
     ========================================================= */

  document.addEventListener(
    'keydown',
    (event) => {

      if (
        event.key === 'Escape' &&
        overlay.classList.contains(
          'is-open'
        )
      ) {

        closeFinder();
      }
    }
  );


  /* =========================================================
     INITIALIZE
     ========================================================= */

  function initialize() {

    document.body.appendChild(
      overlay
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );
  }


  else {

    initialize();
  }

})();